<script>
  import ConfirmationModal from '$lib/components/ConfirmationModal.svelte';
  import Icon from '$lib/components/Icon.svelte';
  import { closeModal, showModal } from '$lib/features/app/modals';
  import { getEvent } from '$lib/features/calendar/services';
  import NewNoteForm from '$lib/features/clients/components/notes/NewNoteForm.svelte';
  import Note from '$lib/features/clients/components/notes/Note.svelte';
  import { notesQueryOptions } from '$lib/features/clients/service';
  import {
    activeCalendarEvent,
    closeVideo,
    videoExpanded,
    videoNote,
  } from '$lib/features/video-meeting/store';
  import { createQuery } from '@tanstack/svelte-query';
  import { derived as derivedStore } from 'svelte/store';
  import { twMerge } from 'tailwind-merge';
  import VideoMeeting from './VideoMeeting.svelte';
  import VideoPatientStatusPanel from './VideoPatientStatusPanel.svelte';
  let meetingStarted = $state(false);
  let width = $derived($videoExpanded ? '480px' : '0');

  const event = createQuery(
    derivedStore(activeCalendarEvent, ($active) => ({
      queryKey: ['event', $active?.id],
      queryFn: () => getEvent($active.id),
      enabled: !!$active?.id,
    })),
  );

  const notesQuery = createQuery(
    derivedStore(event, ($ev) => ({
      ...notesQueryOptions($ev?.data?.clientId),
      enabled: !!$ev?.data?.clientId,
    })),
  );

  /**
   * Sort notes by date created
   */
  let notes = $derived(
    $notesQuery?.data?.notes
      .flat()
      .sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated)) || [],
  );

  let latestNote = $derived($notesQuery?.data?.notes[0]);

  function resize(e) {
    const ww = window.innerWidth;
    width = `${ww - e.clientX}px`;
  }

  /**
   * Stops the resizing of the video sidebar.
   */
  function stopResize() {
    document.removeEventListener('mousemove', resize);
    document.removeEventListener('mouseup', stopResize);
    document.body.classList.remove('disable-select');
  }

  /**
   * Starts the resizing of the video sidebar.
   */
  function startResize() {
    document.addEventListener('mousemove', resize);
    document.addEventListener('mouseup', stopResize);
    document.body.classList.add('disable-select');
  }

  /**
   * End the video call.
   */
  function endCall() {
    closeVideo();
    $videoNote = '';
    $videoExpanded = false;
    closeModal();
  }

  function onCloseVideoCallClick() {
    if (!meetingStarted) {
      // We can close the video call without confirmation if the meeting hasn't started
      endCall();
      return;
    }

    showModal(ConfirmationModal, {
      title: 'Avsluta videomöte',
      text: 'Är du säker på att du vill avsluta videomötet?',
      onConfirm: endCall,
    });
  }
</script>

<div
  class={twMerge(
    'sticky top-0 -mr-px flex h-full max-h-screen flex-col',
    !$videoExpanded && 'hidden',
  )}
  style={`width: ${width}`}
>
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="absolute bottom-0 top-0 w-2 cursor-col-resize border-l border-gray-8 hover:border-gray-7"
    onmousedown={startResize}
  ></div>
  <div class="flex items-center justify-between gap-4 p-4">
    {#if $event?.data?.firstname}
      <a class="text-lg transition" href="/clients/{$event?.data?.clientId}">
        {$event?.data?.firstname}
        {$event?.data?.lastname}
      </a>
    {/if}
    <button onclick={onCloseVideoCallClick}>
      <Icon icon="close" />
    </button>
  </div>
  <VideoPatientStatusPanel />
  {#if $videoExpanded}
    <div class="flex flex-1 flex-col gap-4 overflow-hidden p-4">
      <div>
        {#if $activeCalendarEvent}
          {#key $activeCalendarEvent.id}
            <VideoMeeting
              bind:meetingStarted
              meetingId={$activeCalendarEvent.id}
              event={$event}
            />
          {/key}
        {/if}
      </div>

      <div class="flex-1 overflow-y-auto">
        <div class="mb-1 font-mono">Anteckningar</div>
        <div class="overflow-y-auto">
          {#if notes.length}
            <div class="space-y-2">
              {#each notes as note}
                <Note
                  {note}
                  areaOptions={$notesQuery?.data?.consultationAreas}
                />
              {/each}
            </div>
          {:else}
            <div class="italic">Inga anteckningar</div>
          {/if}
        </div>
      </div>

      <div>
        <div class="mb-1 font-mono">Ny anteckning</div>
        <NewNoteForm
          clientId={$event?.data?.clientId}
          reportId={latestNote?.reportId}
          areaOptions={$notesQuery?.data?.consultationAreas}
          onsave={() => $notesQuery.refetch()}
        />
      </div>
    </div>
  {/if}
</div>
